import * as React from 'react'
import { Deal, DealColumnConfig, DealColumnProperty, DealColumns, DealProperty, ResourceRendererOptions, ResourceRendererResult, Settings } from '../../types'
import { ResourceTableHeader } from './ResourceTable'
import ResourceTableRow from './ResourceTableRow'
import ResourceTableRowData from './ResourceTableRowData'
import ResourceTableRowActions from './ResourceTableRowActions'
import { IActionListItem } from '../ActionList/ActionList'
import i18n from '../../translations'
import LocalStorage, { LocalStorageKey } from '../../LocalStorage'
import NumberFormatter from '../../utilities/NumberFormatter'
import moment from '../../utilities/Moment'
import showdown from '../../utilities/showdown'
import DealHelper from '../../helpers/DealHelper'
import Avatar from '../Avatar/Avatar'
import AvatarStack from '../Avatar/AvatarStack'

export const DEFAULT_DEAL_COLUMNS: DealColumnProperty[] = [
  'number',
  'name',
  'contact',
  'assignee',
  'deal_stage',
]

const DealColumnConfigs: Record<DealColumnProperty, DealColumnConfig> = {
  id: {
    header: () => ({ title: i18n.t('Deal::id') }),
    render: ({ deal }) => deal.id || '-'
  },
  name: {
    header: () => ({ title: i18n.t('Deal::name') }),
    render: ({ deal }) => deal.name || '-',
  },
  number: {
    header: () => ({ title: i18n.t('Deal::number') }),
    render: ({ deal }) => deal.number || '-'
  },
  notes: {
    header: () => ({ title: i18n.t('Deal::notes') }),
    render: ({ deal }) => <div dangerouslySetInnerHTML={{ __html: showdown.makeHtml(deal.notes) }} />
  },
  amount: {
    header: () => ({ title: i18n.t('Deal::amount') }),
    render: ({ deal, setting }) => NumberFormatter.formatCurrency(deal.currency || setting.default_currency, setting.number_format, deal.amount)
  },
  currency: {
    header: () => ({ title: i18n.t('Deal::currency') }),
    render: ({ deal, setting }) => deal.currency || setting.default_currency || '-'
  },
  status: {
    header: () => ({ title: i18n.t('Deal::status') }),
    render: ({ deal }) => deal.status ? i18n.t(`DealStatus::${deal.status}`) : '-'
  },
  contact: {
    header: () => ({ title: i18n.t('Deal::contact') }),
    render: ({ deal }) => deal.contact?.name || '-'
  },
  project: {
    header: () => ({ title: i18n.t('Deal::project') }),
    render: ({ deal }) => deal.project?.name || '-'
  },
  due_on: {
    header: () => ({ title: i18n.t('Deal::due_on') }),
    render: ({ deal, setting }) => deal.due_on ? moment(deal.due_on).format(setting.date_format) : '-'
  },
  deal_stage: {
    header: () => ({ title: i18n.t('Deal::deal_stage') }),
    render: ({ deal }) => deal.deal_stage?.name || '-'
  },
  assignee: {
    header: () => ({ title: i18n.t('Deal::assignee') }),
    render: ({ deal }) => deal.assignee ? (
      <AvatarStack width={30}>
        <Avatar name={deal.assignee.name} width={30} rounded />
      </AvatarStack>
    ) : '-'
  },
  created_at: {
    header: () => ({ title: i18n.t('Deal::created_at') }),
    render: ({ deal, setting }) => deal.created_at ? moment(deal.created_at).format(setting.date_format) : '-'
  },
}

export function getDealTableRenderer(options: ResourceRendererOptions<Deal>): ResourceRendererResult<Deal, DealProperty> {
  const {
    setting,
    onRowClick,
    selectedItems = [],
    onSelectionChange,
    onActionClick
  } = options

  const columns: { label: string, value: string }[] = DealColumns.map(column => ({ label: i18n.t(`Deal::${column}`), value: column }))
  const selectedColumns: DealProperty[] = LocalStorage
    .get(LocalStorageKey.DEAL_COLUMNS, DEFAULT_DEAL_COLUMNS)
    .filter((column) => DealColumnConfigs[column])

  const headers: ResourceTableHeader[] = selectedColumns
    .map(column => ({
      ...DealColumnConfigs[column].header(),
      visible: true
    }))

  headers.push({ title: '', stickyRight: '0px' })

  const renderRow = (deal: Deal, _index: number): React.ReactElement => {
    const isSelected = selectedItems.includes(deal.id)
    let actions: IActionListItem[] = [
      { key: 'update', icon: 'edit-solid', content: i18n.t('DealsTable::Edit') },
      { key: 'delete', icon: 'trash-alt-solid', content: i18n.t('DealsTable::Delete'), destructive: true }
    ]

    const applyMaxWidthColumns: DealProperty[] = [
      'name',
      'notes',
      'contact',
      'project'
    ]

    let cells: React.ReactNode[] = selectedColumns.map((column) => (
      <ResourceTableRowData
        key={`${deal.id}-${column}`}
        onClick={onRowClick ? () => onRowClick(deal) : undefined}
        maxWidth={applyMaxWidthColumns.includes(column) ? '300px' : undefined}
        ellipse={applyMaxWidthColumns.includes(column)}
        textAlign={DealColumnConfigs[column].header().align}
      >
        {DealColumnConfigs[column].render({ deal, ...options })}
      </ResourceTableRowData>
    ))

    // Add actions cell if actions are provided
    if (actions?.length > 0 && onActionClick) {
      cells = [
        ...cells,
        <ResourceTableRowActions
          key={`${deal.id}-actions`}
          actions={actions}
          onActionClick={(key) => onActionClick(key, deal)}
          sticky={true}
          stickyRight='0px'
        />
      ]
    }

    return (
      <ResourceTableRow
        key={deal.id}
        selected={isSelected}
        onSelectionChange={onSelectionChange ? (selected) => onSelectionChange(selected, deal.id) : undefined}
      >
        {cells}
      </ResourceTableRow>
    )
  }

  return {
    headers: headers,
    columns: columns,
    selectedColumns: selectedColumns,
    renderRow: renderRow,
  }
}

export default getDealTableRenderer
